import { CommonModule, Location } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from '../../../material/material.module';
import { NewScoutModel } from '../../../shared/models/user.model';
import { UserService } from '../../../shared/services/user.service';

@Component({
  selector: 'app-new-scout',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, TranslatePipe],
  templateUrl: './new-scout.component.html',
  styleUrl: './new-scout.component.css'
})
export class NewScoutComponent {
  readonly model: NewScoutModel = this.createEmptyModel();
  isSaving = false;

  errorMessage: string | null = null;

  readonly roleControls: ReadonlyArray<{ field: keyof NewScoutModel; labelKey: string }> = [
    { field: 'RoleAllReports', labelKey: 'NEW_SCOUT.ROLE_ALL_REPORTS' },
    { field: 'RoleAllGameRatings', labelKey: 'NEW_SCOUT.ROLE_ALL_GAME_RATINGS' },
    { field: 'RoleAllCalendarEvents', labelKey: 'NEW_SCOUT.ROLE_ALL_CALENDAR_EVENTS' },
    { field: 'RoleOrgadmin', labelKey: 'NEW_SCOUT.ROLE_ORG_ADMIN' },
    { field: 'RoleOrgPlayerAdmin', labelKey: 'NEW_SCOUT.ROLE_ORG_PLAYER_ADMIN' },
    { field: 'RoleAllPlayerTags', labelKey: 'NEW_SCOUT.ROLE_ALL_PLAYER_TAGS' },
    { field: 'RoleStatisticsEditor', labelKey: 'NEW_SCOUT.ROLE_STATISTICS_EDITOR' },
    { field: 'RoleStatisticsViewer', labelKey: 'NEW_SCOUT.ROLE_STATISTICS_VIEWER' },
  ];

  constructor(
    private userService: UserService,
    private location: Location,
    private router: Router,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
  ) {}

  back(): void {
    this.location.back();
  }

  isEmailInvalid(): boolean {
    const value = (this.model.NewScoutemail || '').trim();
    if (!value) {
      return false;
    }

    return !/^\S+@\S+\.\S+$/.test(value);
  }

  isPasswordInvalid(): boolean {
    const length = (this.model.NewScoutPassword || '').length;
    return length > 0 && (length < 6 || length > 255);
  }

  isPasswordMismatch(): boolean {
    const password = this.model.NewScoutPassword || '';
    const confirm = this.model.NewScoutPasswordConfirm || '';

    if (!password || !confirm) {
      return false;
    }

    return password !== confirm;
  }

  canCreate(): boolean {
    return !!(this.model.UserName || '').trim()
      && !!(this.model.FirstName || '').trim()
      && !!(this.model.LastName || '').trim()
      && !!(this.model.NewScoutemail || '').trim()
      && !!(this.model.NewScoutPassword || '').trim()
      && !!(this.model.NewScoutPasswordConfirm || '').trim()
      //&& !!this.model.OrganizationId
      && !this.isEmailInvalid()
      && !this.isPasswordInvalid()
      && !this.isPasswordMismatch();
  }

  createScout(): void {
    if (!this.canCreate() || this.isSaving) {
      return;
    }

    this.isSaving = true;

    this.errorMessage = null;

    this.userService.createScout(this.model).subscribe({
      next: () => {
        this.isSaving = false;
        this.snackBar.open(this.translate.instant('NEW_SCOUT.SUCCESS'), undefined, { duration: 2500 });
        this.router.navigate(['/admin/scouts']);
      },
      error: (err) => {
        this.isSaving = false;
        // console.error('Error creating scout:', err);
        if (err?.status === 400 && err?.error) {
          // Accept both 'Message' and 'message' keys from backend
          if (typeof err.error === 'object' && (err.error.Message || err.error.message)) {
            this.errorMessage = err.error.Message || err.error.message;
          } else if (typeof err.error === 'string') {
            this.errorMessage = err.error;
          } else {
            this.errorMessage = this.translate.instant('NEW_SCOUT.ERROR');
          }
        } else {
          this.errorMessage = this.translate.instant('NEW_SCOUT.ERROR');
        }
        this.snackBar.open(this.errorMessage ?? this.translate.instant('NEW_SCOUT.ERROR'), undefined, { duration: 3500 });
      }
    });
  }

  private createEmptyModel(): NewScoutModel {
    const organizationIdValue = localStorage.getItem('OrganizationId') || '';
    const organizationId = Number.parseInt(organizationIdValue, 10);

    return {
      UserName: '',
      OrganizationId: undefined,
      FirstName: '',
      LastName: '',
      NewScoutPassword: '',
      NewScoutPasswordConfirm: '',
      NewScoutemail: '',
      RoleAllReports: false,
      RoleAllGameRatings: false,
      RoleAllCalendarEvents: false,
      RoleOrgadmin: false,
      RoleOrgPlayerAdmin: false,
      RoleAllPlayerTags: false,
      RoleStatisticsEditor: false,
      RoleStatisticsViewer: false,
      Module_PlayerTags: false,
      UserSecurity: [],
    };
  }
}
